import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, RotateCcw, Check, ArrowRight } from 'lucide-react';
import { scoreQuiz } from '../data/quizzes';
import { isLoggedIn, saveQuizResult } from '../api';
import { toast } from '../hooks/use-toast';

const COLORS = ['#f0d582', '#c9a2d4', '#7bb661', '#ff8fb3', '#8fb8ff', '#f5b971', '#9be3d6'];

export const QuizRunner = ({ quiz, onSaved, onClose }) => {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const pick = (type) => {
    const next = [...answers]; next[i] = type; setAnswers(next);
    setTimeout(() => {
      if (i < quiz.questions.length - 1) setI(i + 1);
      else setResult(scoreQuiz(quiz, next));
    }, 250);
  };

  const retake = () => { setI(0); setAnswers([]); setResult(null); setSaved(false); };

  const save = async () => {
    if (!isLoggedIn()) {
      localStorage.setItem('pe_pending_quiz', JSON.stringify({ quiz_key: quiz.key, result }));
      toast({ title: 'Sign in to save', description: 'Log in or create a free account and your result will be added to your Wellness Profile.' });
      onClose && onClose();
      navigate('/account');
      return;
    }
    setSaving(true);
    try {
      await saveQuizResult(quiz.key, result);
      setSaved(true);
      toast({ title: 'Saved to your Wellness Profile' });
      onSaved && onSaved();
    } catch (e) {
      toast({ title: 'Could not save', description: e?.response?.data?.detail });
    } finally { setSaving(false); }
  };

  if (result) {
    const entries = Object.entries(result.breakdown).sort((a, b) => b[1] - a[1]);
    return (
      <div data-testid={`quiz-result-${quiz.key}`} className="text-center">
        <Sparkles className="h-10 w-10 text-[#f0d582] mx-auto mb-3" />
        <p className="font-jost text-[11px] uppercase tracking-[0.3em] text-[#f5ecd9]/50">Your result</p>
        <h3 className="font-cinzel text-2xl sm:text-3xl text-gold-gradient mt-1" data-testid="quiz-primary">{result.primary_label}</h3>
        {result.secondary_label && <p className="font-cormorant text-lg text-[#f5ecd9]/70 mt-1">with {result.secondary_label} influences</p>}
        <p className="font-jost text-sm text-[#f5ecd9]/70 max-w-md mx-auto mt-3">{quiz.blurbs[result.primary]}</p>
        <div className="mt-6 space-y-2 max-w-sm mx-auto text-left">
          {entries.map(([k, v], idx) => (
            <div key={k}>
              <div className="flex justify-between font-jost text-xs text-[#f5ecd9]/60 mb-1"><span>{quiz.labels[k]}</span><span>{v}%</span></div>
              <div className="h-2 rounded-full bg-white/8 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${v}%`, background: COLORS[idx % COLORS.length] }} /></div>
            </div>
          ))}
        </div>
        {quiz.reflection && <p className="font-jost text-[11px] text-[#f5ecd9]/45 max-w-md mx-auto mt-5 italic">{quiz.reflection}</p>}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-7">
          {!saved ? (
            <button data-testid="quiz-save-btn" onClick={save} disabled={saving} className="btn-gold rounded-full px-6 py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582]"><Check className="h-4 w-4 inline mr-1" /> {isLoggedIn() ? 'Save to my Wellness Profile' : 'Sign in to save'}</button>
          ) : (
            <Link to="/account" onClick={onClose} className="btn-gold rounded-full px-6 py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582]">View my profile <ArrowRight className="h-4 w-4 inline ml-1" /></Link>
          )}
          <button data-testid="quiz-retake-btn" onClick={retake} className="rounded-full px-5 py-3 text-sm font-jost text-[#f5ecd9]/70 border border-white/15 hover:border-[#d4af37]/40"><RotateCcw className="h-4 w-4 inline mr-1" /> Retake</button>
        </div>
        {!isLoggedIn() && <p className="font-jost text-[11px] text-[#f5ecd9]/40 mt-4">You can explore your result freely. <Link to="/account" onClick={() => localStorage.setItem('pe_pending_quiz', JSON.stringify({ quiz_key: quiz.key, result }))} className="underline text-[#f0d582]">Create a free account</Link> to save it and get personalized recommendations.</p>}
      </div>
    );
  }

  const question = quiz.questions[i];
  return (
    <div data-testid={`quiz-run-${quiz.key}`}>
      <p className="font-jost text-[11px] uppercase tracking-[0.3em] text-[#f5ecd9]/45 text-center">{quiz.title} · {i + 1} / {quiz.questions.length}</p>
      <div className="h-1 rounded-full bg-white/8 my-4 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#d4af37] to-[#f0d582] transition-all" style={{ width: `${((i) / quiz.questions.length) * 100}%` }} /></div>
      <h3 className="font-cinzel text-xl sm:text-2xl text-[#f5ecd9] text-center mb-6">{question.q}</h3>
      <div className="grid gap-3 max-w-md mx-auto">
        {question.options.map((o, idx) => (
          <button key={idx} data-testid={`quiz-option-${idx}`} onClick={() => pick(o.type)} className="text-left rounded-xl px-5 py-4 font-jost text-sm text-[#f5ecd9]/85 border border-white/10 bg-white/[0.03] hover:border-[#d4af37]/50 hover:bg-[#d4af37]/5 transition-all">{o.label}</button>
        ))}
      </div>
    </div>
  );
};

export default QuizRunner;
